/**
 * Crafting System Data
 * Re-exports all crafting recipes and configuration
 */

export * from './config';
export * from './equipmentRecipes';
export * from './consumableRecipes';
export * from './materialRecipes';

import { CRAFTING_CONFIG, CRAFTING_CATEGORIES } from './config';
import { EQUIPMENT_RECIPES } from './equipmentRecipes';
import { CONSUMABLE_RECIPES } from './consumableRecipes';
import { MATERIAL_RECIPES } from './materialRecipes';

// ============================================
// COST MULTIPLIER - Apply 10x to all recipes
// ============================================
const CRAFTING_COST_MULTIPLIER = 10;

/**
 * Apply cost multiplier to a recipe
 */
function applyMultiplier(recipe) {
  const newCost = {
    resources: {},
    materials: {},
  };

  // Multiply all resource costs
  if (recipe.cost?.resources) {
    for (const [res, amount] of Object.entries(recipe.cost.resources)) {
      newCost.resources[res] = amount * CRAFTING_COST_MULTIPLIER;
    }
  }

  // Multiply all material costs
  if (recipe.cost?.materials) {
    for (const [mat, amount] of Object.entries(recipe.cost.materials)) {
      newCost.materials[mat] = amount * CRAFTING_COST_MULTIPLIER;
    }
  }

  return {
    ...recipe,
    cost: newCost,
  };
}

/**
 * Apply multiplier to all recipes in an object
 */
function applyMultiplierToRecipes(recipes) {
  const result = {};
  for (const [id, recipe] of Object.entries(recipes)) {
    result[id] = applyMultiplier(recipe);
  }
  return result;
}

// Combined recipes object with 10x cost multiplier applied
export const ALL_RECIPES = {
  ...applyMultiplierToRecipes(EQUIPMENT_RECIPES),
  ...applyMultiplierToRecipes(CONSUMABLE_RECIPES),
  ...applyMultiplierToRecipes(MATERIAL_RECIPES),
};

/**
 * Get recipes by category
 * @param {string} category - 'equipment' | 'consumables' | 'materials'
 * @returns {Array} Array of recipe objects
 */
export function getRecipesByCategory(category) {
  return Object.values(ALL_RECIPES).filter(r => r.category === category);
}

/**
 * Get recipe by ID
 * @param {string} recipeId - Recipe ID
 * @returns {Object|null} Recipe object or null
 */
export function getRecipe(recipeId) {
  return ALL_RECIPES[recipeId] || null;
}

/**
 * Get all available recipes (filtered by tech/buildings)
 * @param {Array} researchedTechs - Array of researched tech IDs
 * @param {Object} buildingCounts - Object of buildingId -> count
 * @returns {Array} Array of available recipe objects
 */
export function getAvailableRecipes(researchedTechs = [], buildingCounts = {}) {
  return Object.values(ALL_RECIPES).filter(recipe => {
    // Check tech requirement
    if (recipe.requiresTech && !researchedTechs.includes(recipe.requiresTech)) {
      return false;
    }
    // Check building requirement
    if (recipe.requiresBuilding) {
      for (const [buildingId, count] of Object.entries(recipe.requiresBuilding)) {
        if ((buildingCounts[buildingId] || 0) < count) {
          return false;
        }
      }
    }
    return true;
  });
}

/**
 * Get available recipes by category
 * @param {string} category - 'equipment' | 'consumables' | 'materials'
 * @param {Array} researchedTechs - Array of researched tech IDs
 * @param {Object} buildingCounts - Object of buildingId -> count
 * @returns {Array} Array of available recipe objects in category
 */
export function getAvailableRecipesByCategory(category, researchedTechs = [], buildingCounts = {}) {
  return getAvailableRecipes(researchedTechs, buildingCounts).filter(r => r.category === category);
}

/**
 * Count available recipes by category
 * @param {Array} researchedTechs - Array of researched tech IDs
 * @param {Object} buildingCounts - Object of buildingId -> count
 * @returns {Object} Object of category -> count
 */
export function countAvailableByCategory(researchedTechs = [], buildingCounts = {}) {
  const available = getAvailableRecipes(researchedTechs, buildingCounts);
  return {
    equipment: available.filter(r => r.category === 'equipment').length,
    consumables: available.filter(r => r.category === 'consumables').length,
    materials: available.filter(r => r.category === 'materials').length,
  };
}

export default {
  CRAFTING_CONFIG,
  CRAFTING_CATEGORIES,
  ALL_RECIPES,
  getRecipesByCategory,
  getRecipe,
  getAvailableRecipes,
  getAvailableRecipesByCategory,
  countAvailableByCategory,
};
